import { useState } from 'react'
import { usePersonasList, useEffectiveAccess } from '@/api/queries'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { PublishSetFlow } from '@/components/PublishSetFlow'
import { useQueryClient } from '@tanstack/react-query'

interface Props { onEdit: (role: string) => void }

export function Drafts({ onEdit }: Props) {
  const q = usePersonasList()
  const access = useEffectiveAccess()
  const qc = useQueryClient()
  const drafts = (q.data?.items ?? []).filter((p) => p.status === 'Draft')
  const canPublish = (access.data?.capabilities ?? []).includes('persona.publish')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [setPublishing, setSetPublishing] = useState<string[] | null>(null)

  const toggle = (role: string) => {
    const next = new Set(selected)
    if (next.has(role)) next.delete(role); else next.add(role)
    setSelected(next)
  }

  if (q.isLoading) return <div className="page"><p>Loading drafts…</p></div>
  return (
    <div className="page">
      <div className="page-head">
        <div><h1>Drafts</h1><p>{drafts.length} unpublished change(s)</p></div>
        {selected.size >= 2 && (
          <Button variant="primary" disabled={!canPublish}
            title={!canPublish ? 'Your role lacks persona.publish' : ''}
            onClick={() => setSetPublishing(Array.from(selected))}>
            Publish {selected.size} together
          </Button>
        )}
      </div>
      {drafts.length === 0 ? (
        <div className="empty">
          <p>No drafts — every persona is published and in sync with the bridge.</p>
        </div>
      ) : (
        <div className="stack-12">
          {drafts.map((d) => (
            <div key={d.role} className="card pad" style={{
              display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 'var(--sp-16)', alignItems: 'center',
            }}>
              <input
                type="checkbox"
                checked={selected.has(d.role)}
                onChange={() => toggle(d.role)}
                aria-label={`Select ${d.role} for multi-publish`}
              />
              <div>
                <div className="row" style={{ gap: 10 }}>
                  <span className="mono" style={{ fontWeight: 600 }}>{d.role}</span>
                  <Badge tone="warn">Unpublished draft</Badge>
                </div>
                <div className="muted" style={{ fontSize: 'var(--fs-200)', marginTop: 4 }}>
                  Last modified by {d.lastModifiedBy}
                </div>
              </div>
              <div className="row" style={{ gap: 8 }}>
                <Button onClick={() => onEdit(d.role)}>Edit</Button>
                <Button
                  variant="primary"
                  disabled={!canPublish}
                  title={!canPublish ? 'Your role lacks persona.publish' : ''}
                  onClick={() => onEdit(d.role)}
                >Publish…</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {setPublishing && (
        <PublishSetFlow
          roles={setPublishing}
          onClose={() => setSetPublishing(null)}
          onDone={() => { setSelected(new Set()); qc.invalidateQueries({ queryKey: ['personas'] }) }}
        />
      )}
    </div>
  )
}
